import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { loadAppearance } from "./appearance.ts";
import "./styles.css";

/**
 * Trusted Types (CSP `require-trusted-types-for 'script'`): the app writes no HTML, but
 * react-aria's screen-reader announcer empties its live region with `innerHTML = ""`. This
 * default policy lets exactly that through, and MapLibre's worker script (ADR-0071), one of the
 * app's own built files, and refuses everything else.
 */
const trusted = (
  window as unknown as {
    trustedTypes?: {
      createPolicy: (
        name: string,
        rules: {
          createHTML: (html: string) => string;
          createScriptURL: (url: string) => string;
        },
      ) => unknown;
    };
  }
).trustedTypes;
trusted?.createPolicy("default", {
  createHTML: (html) => {
    if (html === "") return html;
    throw new TypeError("Writing HTML is not allowed");
  },
  createScriptURL: (url) => {
    const u = new URL(url, window.location.href);
    const file = u.pathname.split("/").pop() ?? "";
    if (
      u.origin === window.location.origin &&
      u.pathname.startsWith("/assets/") &&
      file.startsWith("maplibre-gl-csp-worker") &&
      file.endsWith(".js")
    ) {
      return u.href;
    }
    throw new TypeError("Loading scripts is not allowed");
  },
});

loadAppearance();

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
